import { types, SnapshotOut, Instance } from 'mobx-state-tree';
import { observable } from 'mobx';

import { IEvent, IGameGroup, SelectionVisualisationFlag } from './../api/proto/apiContract';
import { OddModel } from './odd';
import { OddsListItem } from './../dtos/apiWebStandardEventDtos';
import { EventsItem } from './../dtos/apiLiveEventsDtos';
import { IGame, OfferItemState, GameBusinessType, IOddsPopularityStats } from '../api/proto/apiContract';
import { protoUtils } from '../api/apiCommon';
import { LiveEventStatus } from '../enums';
import { getLongTipNameRaw, canConvertTipName, IOddsModelCreator, IRenameTipDataProvider, IRenameTipData } from '../utils/eventsUtils';
import { StatisticsInApiStore } from './statistics';
import { StatisticsType } from '../dtos/apiLiveEventsDtos';
import { OddsPopularityStatsInApiStore } from './oddsPopularityStats';

export let rootCategoryId: string = __RootCategoryId__;
let canSendRootCategoryId: boolean = __CanSendRootCategoryId__; // send root as value to api (false means as null)

export const rootCategoryIdToNull = (categoryId?: string | null): string | null => {
    if (canSendRootCategoryId) {
        return categoryId;
    }
    if (categoryId == rootCategoryId || !categoryId) {
        return null;
    }
    return categoryId;
}

/**
 * Creates key joining ids by c (many places has its own implementation that's not correct)
 */
export const createCategoryKey = (ids: string[]): string => {
    return ids.join("c");
}

export const gameDetailStore = types.model({
    id: types.maybeNull(types.number),
    name: types.maybeNull(types.string),
    number: types.maybeNull(types.number),
    odds: types.optional(types.array(OddModel), [])
}).views((self) => {
    return {
        get hasCustomTextInOdds() {
            return !self.odds.some(v => !v.hasCustomText);
        }
    }
});

export const gameStore = types.model({
    id: types.string,
    details: types.array(gameDetailStore),
    name: types.maybeNull(types.string),
    combRateMin: types.maybeNull(types.number),
    combCountMin: types.maybeNull(types.number),
    canBePlayedAsSystemBet: types.maybeNull(types.boolean),
    stakeMax: types.maybeNull(types.number),
    hasDetails: types.maybeNull(types.boolean),
    isInitial: types.maybeNull(types.boolean),  /* for MTS desktop live only, until SelectionVisualisationFlags has correct game type value */
    info: types.maybeNull(types.string)
}).views((self) => {
    return {
        get showInfoIcon() {
            return !self.canBePlayedAsSystemBet || !!self.combCountMin || !!self.combRateMin || !!self.stakeMax ? true : false;
        }
    }
});

export const gameGroupStore = types
    .model({
        games: types.array(gameStore),
        name: types.maybeNull(types.string),
        id: types.string,
        sequence: types.number
    });


export type IGameGroupStore = Instance<typeof gameGroupStore>;

export const BaseEventModel = types.model({
    id: types.maybeNull(types.number),
    name: types.maybeNull(types.string),
    gameGroups: types.maybeNull(types.array(gameGroupStore)),
    categoryPath: types.maybeNull(types.string),
    graphicsPresentationId: types.maybeNull(types.number),
    startDate: types.maybeNull(types.Date),
    info: types.maybeNull(types.string),
    marketingMessage: types.maybeNull(types.string),
    detailsCount: types.maybeNull(types.number),
    statistics: types.maybeNull(StatisticsInApiStore),
    superOddsPopularityStats: types.maybeNull(types.array(OddsPopularityStatsInApiStore)),
}).views((_self) => {
    //const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);

    return {
        canConvertTipName(game: IGame): boolean {
            return canConvertTipName(game);
        }
    }
})
    .actions(self => {
        const setBaseData = (event: IEvent, renameTipDataProvider: IRenameTipDataProvider, createOddModel: IOddsModelCreator) => {
            //! eventListModel.ts and topbets does not use this!
            self.id = event.Id;
            self.startDate = protoUtils.protoTimeStamp2Date(event.Date);
            self.name = event.Name;
            self.gameGroups = observable(createGameGroups(event.GameGroups, event.Id, false, renameTipDataProvider, createOddModel, false));
            self.info = event.Info;
            self.marketingMessage = event.MarketingMessage;
            self.graphicsPresentationId = event.GraphicsPresentationId;
            self.superOddsPopularityStats = observable(event.SuperOddsPopularityStats.map((x) => createOddsPopularityStatsModel(x)));
        };

        const setBaseLiveData = (event: EventsItem, renameTipDataProvider: IRenameTipDataProvider, createOddModel: IOddsModelCreator) => {
            self.id = event.ID;
            self.name = event.Name;
            self.categoryPath = event.CategoryPath;
            self.graphicsPresentationId = event.GraphicsPresentationID;
            self.detailsCount = event.DetailsCount;

            if (event.Stats) {
                self.statistics = StatisticsInApiStore.create(<SnapshotOut<typeof StatisticsInApiStore>>{ id: event.Stats.Id, type: event.Stats.Type === undefined ? StatisticsType.BRMatch : event.Stats.Type });
            } else {
                self.statistics = null;
            }

            self.gameGroups = observable(createGameGroups(event.GameGroups, event.ID, event.State === LiveEventStatus.Suspended, renameTipDataProvider, createOddModel, true));
        }

        const createGameGroups = (gameGroups: IGameGroup[], eventId: number, isEventSuspended: boolean, renameTipDataProvider: IRenameTipDataProvider, createOddModel: IOddsModelCreator, isForLive: boolean) => {

            let groupSequence = 0;
            return gameGroups.map(gameGroup => {
                return gameGroupStore.create(<SnapshotOut<typeof gameGroupStore>>{
                    id: `${gameGroup.ID}`,
                    name: gameGroup.Name,
                    sequence: groupSequence++,
                    games: gameGroup.Games.map(game => {
                        let canConvertTipName = self.canConvertTipName(game);
                        let renameTipData = renameTipDataProvider.get();
                        return gameStore.create(<SnapshotOut<typeof gameStore>>{
                            id: (!__IsMTS__ || __UseFakeData__ /* TODO change game ids to string in fake data */) && typeof (game.ID) === "number" ? (<number>game.ID).toString() : game.ID,  //!! HACK: for Tipos mobile web - Live, game ids comes as number since it's not possible to use string due to compatibility.
                            name: game.Name,
                            hasDetails: game.IsDetails,
                            details: game.Details.map(gameDetail =>
                                gameDetailStore.create(<SnapshotOut<typeof gameDetailStore>>{
                                    id: gameDetail.ID,
                                    name: gameDetail.Name,
                                    odds: gameDetail.OddsList.map((o) => {
                                        /* for tests of space in branchtv
                                        if (gameDetail.OddsList.length == 3) {
                                            if (index == 0) {
                                                o.Name += " w" + (Math.random() > .5 ? "1" : "");
                                            } else if (index == 1) {
                                                o.Name += " w1w2";
                                            } else if (index == 2) {
                                                o.Name += " w1w2w4";
                                            }
                                        } else if (gameDetail.OddsList.length == 2) {
                                            if (index == 0) {
                                                o.Name += " wh1d abcdefghjiks" + (Math.random() > .5 ? "1" : "");
                                            } else if (index == 1) {
                                                o.Name += " w1w2edf23" + (Math.random() > .5 ? "x" : "");
                                            }
                                        }*/
                                        return createOddModel(o, game, gameDetail, eventId, isEventSuspended, canConvertTipName, renameTipData);
                                    }),
                                    number: gameDetail.LineNumber //used in MTS only!
                                })
                            ),
                            combRateMin: game.Details.length ? game.Details[0].CombRateMin : undefined,
                            combCountMin: game.Details.length ? game.Details[0].CombCountMin : undefined,
                            canBePlayedAsSystemBet: game.Details.length ? game.Details[0].CanBePlayedAsSystemBet : undefined,
                            stakeMax: game.Details.length ? game.Details[0].StakeMax : undefined,
                            isInitial: __IsMTS__ && isForLive ? (<{ Initial: boolean }>game).Initial : undefined,
                            info: game.Info
                        })
                    }
                    )
                })
            });
        }

        return {
            setBaseData,
            setBaseLiveData
        }
    });

export type BaseEventModelType = Instance<typeof BaseEventModel>;

export interface IParsedTipIdent {
    eventID: number;
    gameID: string;
    detailID: number;
    tipID: string;
    rate: number;
}

const isGameIdRegEx = /^\d+(d\d+)?$/ /* as Api creates compound id  */;

export const isCorrectGameId = (gameId: string): boolean => {
    return isGameIdRegEx.test(gameId);
}

export const parseTipIdent = (ident: string): IParsedTipIdent => {
    const parts = ident.split('-');
    const parts2 = parts[2].split('_');
    return {
        eventID: parseInt(parts[0], 10),
        gameID: isCorrectGameId(parts[1]) ? parts[1] : "NaN" /* was parseInt */,
        detailID: parseInt(parts2[0], 10),
        tipID: parts2[1],
        rate: parseFloat(parts2[2])
    };
};

export const createTipKey = (item: OddsListItem): string => {
    return `${item.EventID}-${item.GameID}-${item.DetailID}_${item.TipID}`;
};

export const createTipIdent = (item: OddsListItem): string => {
    return `${item.EventID}-${item.GameID}-${item.DetailID}_${item.TipID}_${item.Rate.toFixed(2)}-0`;
};

/**
 *
 * @param item
 * @param canConvertTipName
 * @param renameTipData data to rename, used either for odd name itself or for name used in a ticket
 */
export const createOddModel = (item: OddsListItem, isEventSuspended: boolean, gameDetailState: OfferItemState, canConvertTipName: boolean, renameTipData: IRenameTipData): Instance<typeof OddModel> => {
    if (!item) {
        return null;
    }
    let newName: string;
    let hasCustomText: boolean;
    if (canConvertTipName) {
        newName = getLongTipNameRaw(item.Name, renameTipData);
        hasCustomText = newName === item.Name;
    } else {
        newName = item.Name;
        hasCustomText = true;//see comment at OddModel.hasCustomText
    }
    return OddModel.create(<SnapshotOut<typeof OddModel>>{
        name: newName,
        //nameForTicket: TODOH cannot rename automatically for all games (goals)  !__IsMTS__ || canConvertTipName ? undefined /* correct name already in name */ : newName,
        popularity: item.PopularityPercentage,
        rate: item.Rate,
        ident: createTipIdent(item),
        key: createTipKey(item),
        isHighlighted: item.TicketOfDayTip && item.TicketOfDayTip,
        hasCustomText: hasCustomText,
        isSuspended: item.State === OfferItemState.Suspended || isEventSuspended || gameDetailState === OfferItemState.Suspended //game has also State, but as aggregation
        // ad suspended: in UOF detail should reflect event general state, but for sure now (and for Tipos)
    });
};

export const createOddsPopularityStatsModel = (data: IOddsPopularityStats): Instance<typeof OddsPopularityStatsInApiStore> => {
    return OddsPopularityStatsInApiStore.create(<SnapshotOut<typeof OddsPopularityStatsInApiStore>>{
        tipId: data.TipID,
        name: data.Name,
        percentage: data.Percentage
    });
}

export const selectionVisualisationFlags2GameBusinessType = (selectionVisualisationFlags: number): GameBusinessType => {
    if ((selectionVisualisationFlags & SelectionVisualisationFlag.Market1X2) === SelectionVisualisationFlag.Market1X2) {
        return GameBusinessType.Winner_1x2;
    }
    if ((selectionVisualisationFlags & SelectionVisualisationFlag.Market12) === SelectionVisualisationFlag.Market12) {
        return GameBusinessType.Winner_12;
    }
    if ((selectionVisualisationFlags & SelectionVisualisationFlag.MarketHandicap12) === SelectionVisualisationFlag.MarketHandicap12) {
        return GameBusinessType.SpreadOrHandicap12;
    }
    if ((selectionVisualisationFlags & SelectionVisualisationFlag.MarketHandicap1X2) === SelectionVisualisationFlag.MarketHandicap1X2) {
        return GameBusinessType.Handicap1x2;
    }
    if ((selectionVisualisationFlags & SelectionVisualisationFlag.MarketVMT) === SelectionVisualisationFlag.MarketVMT) {
        return GameBusinessType.VMT;
    }
    return GameBusinessType.None
}
